import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Image from '../AppImage';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import NotificationCenter from '../NotificationCenter';


const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isNotificationCenterOpen, setIsNotificationCenterOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navigationItems = [
    {
      name: 'Home',
      path: '/',
      icon: 'Home'
    },
    {
      name: 'Core Data Services',
      path: '/services-overview-interactive-capability-showcase',
      icon: 'Briefcase'
    },
    {
      name: 'Fractional CDO',
      path: '/fractional-cdo-services-executive-leadership-solutions',
      icon: 'Crown'
    },
    {
      name: 'AI Solutions',
      path: '/ai-solutions-intelligent-data-strategy-services',
      icon: 'Sparkles'
    },
    {
      name: 'Free Assessments',
      path: '/free-assessments-hub',
      icon: 'Calculator'
    },
    {
      name: 'Schedule Consultation',
      path: '/schedule-consultation',
      icon: 'Calendar'
    },
    {
      name: 'Request Proposal',
      path: '/request-proposal',
      icon: 'FileText'
    },
    {
      name: 'Availability',
      path: '/availability-management',
      icon: 'Calendar',
      adminOnly: true
    },
    {
      name: 'Calendar Dashboard',
      path: '/calendar-dashboard',
      icon: 'CalendarDays',
      adminOnly: true
    },
    {
      name: 'Consultation Console',
      path: '/consultation-management-console-administrative-dashboard',
      icon: 'LayoutDashboard',
      adminOnly: true
    },
    {
      name: 'Monday Slot Manager',
      path: '/monday-slot-management-console-administrative-time-slot-control-center',
      icon: 'CalendarClock',
      adminOnly: true
    },
    {
      name: 'Workflow Automation',
      path: '/workflow-automation',
      icon: 'Workflow',
      adminOnly: true
    },
    {
      name: 'Analytics Dashboard',
      path: '/real-time-analytics-dashboard-business-intelligence-center',
      icon: 'BarChart3',
      adminOnly: true
    },
    {
      name: 'Chat Analytics',
      path: '/chat-analytics-dashboard-monitoring-center',
      icon: 'MessageSquare',
      adminOnly: true
    },
    {
      name: 'Email Tracking',
      path: '/email-delivery-monitoring-dashboard-resend-integration-command-center',
      icon: 'Mail',
      adminOnly: true
    },
    {
      name: 'Email Test Center',
      path: '/email-test',
      icon: 'TestTube',
      adminOnly: true
    },
    {
      name: 'Knowledge Hub Admin',
      path: '/knowledge-hub-admin',
      icon: 'BookOpen',
      adminOnly: true
    }
  ];

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const handleNavigation = (path) => {
    closeMenu();
    navigate(path);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-subtle border-b border-border' 
          : 'bg-white/90 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-300 pl-6"
            onClick={closeMenu}
          >
            <div className="h-14 lg:h-20">
              <Image
                src="/assets/images/northstar-icon-teal-1024-1771163020674.png"
                alt="Northstar Analytics Logo"
                className="h-full w-auto object-contain"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center nav-spacing">
            {navigationItems?.filter(item => !item?.adminOnly || (item?.adminOnly && userProfile?.role === 'admin'))?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`relative flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:bg-surface hover:text-primary ${
                  item?.promoted
                    ? 'bg-gradient-to-r from-primary/10 to-secondary/10 text-primary border border-primary/20 hover:border-primary/40 hover:shadow-md'
                    : isActivePath(item?.path)
                    ? 'text-primary bg-surface nav-active' :'text-muted-foreground hover:text-primary'
                }`}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.name}</span>
                {item?.promoted && (
                  <span className="absolute -top-1 -right-1 flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* CTA Button - Desktop */}
          <div className="hidden lg:flex items-center space-x-4 pr-4">
            {user ? (
              <>
                <Button
                  variant="default"
                  size="sm"
                  iconName="LayoutDashboard"
                  iconPosition="left"
                  onClick={() => navigate('/client-portal-dashboard-personalized-analytics-hub')}
                  className="hover-lift"
                >
                  Dashboard
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/signin')}
                >
                  Sign In
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Calendar"
                  iconPosition="left"
                  onClick={() => handleNavigation('/contact-multi-channel-engagement-hub')}
                  className="bg-secondary hover:bg-secondary/90 hover-lift"
                >
                  Book Consultation
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 lg:hidden pr-6">
            {user && (
              <button
                onClick={() => setIsNotificationCenterOpen(true)}
                className="relative p-2 rounded-lg hover:bg-surface transition-colors"
                aria-label="Notifications"
              >
                <Icon name="Bell" size={20} className="text-gray-700" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={toggleMenu}
              className="p-2 rounded-lg hover:bg-surface transition-colors"
              aria-label="Toggle menu"
            >
              <Icon name={isMenuOpen ? 'X' : 'Menu'} size={24} className="text-gray-700" />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden transition-all duration-300 overflow-hidden ${
          isMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-4 py-4 bg-white/95 backdrop-blur-md border-t border-border">
            <nav className="space-y-2">
              {navigationItems?.filter(item => !item?.adminOnly || (item?.adminOnly && userProfile?.role === 'admin'))?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={closeMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-all duration-300 touch-target ${
                    item?.promoted
                      ? 'bg-gradient-to-r from-primary/10 to-secondary/10 text-primary border border-primary/20'
                      : isActivePath(item?.path)
                      ? 'text-primary bg-surface' :'text-muted-foreground hover:text-primary hover:bg-surface'
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.name}</span>
                </Link>
              ))}
            </nav>
            
            {/* Mobile CTAs */}
            <div className="mt-6 space-y-3 px-4">
              {user ? (
                <Button
                  variant="default"
                  fullWidth
                  iconName="LayoutDashboard"
                  iconPosition="left"
                  onClick={() => handleNavigation('/client-portal-dashboard-personalized-analytics-hub')}
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    fullWidth
                    onClick={() => handleNavigation('/signin')}
                  >
                    Sign In
                  </Button>
                  <Button
                    variant="default"
                    fullWidth
                    iconName="Calendar"
                    iconPosition="left"
                    onClick={() => handleNavigation('/contact-multi-channel-engagement-hub')}
                    className="bg-secondary hover:bg-secondary/90"
                  >
                    Book Consultation
                  </Button>
                </>
              )}
              {userProfile?.role === 'admin' && (
                <>
                  <a
                    href="/consultation-management-console-administrative-dashboard"
                    className="text-foreground hover:text-primary transition-colors duration-200"
                  >
                    Consultations
                  </a>
                  <a
                    href="/knowledge-hub-admin"
                    className="text-foreground hover:text-primary transition-colors duration-200"
                  >
                    Knowledge Hub Admin
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Notification Center */}
      <NotificationCenter
        isOpen={isNotificationCenterOpen}
        onClose={() => setIsNotificationCenterOpen(false)}
      />
    </header>
  );
};

export default Header;